/**
 * Claim-anchor coverage warning banner.
 *
 * Renders above each section's PaperSurface when the post-
 * generation anchor check (see backend/spec.py
 * `_compute_anchor_check`) flagged independent-claim feature
 * tags the model didn't anchor in this section's paragraphs.
 *
 * Advisory only — does NOT block freeze/export. Drafter can:
 *   - dismiss with the X button (clears for this session)
 *   - regenerate the section (new check repopulates the entry)
 *   - freeze the section (hidden once frozen)
 *   - click "Add coverage for these features →", which APPENDS
 *     support for exactly these features (mode='cover_missing')
 *     without touching the existing draft.
 *
 * That last one used to pre-fill the ActionBar steering panel and
 * arm the next Regenerate, which ran with overwrite=true and
 * DELETEd every unlocked paragraph before writing a fresh draft
 * under the same word cap. So the only wired way to ADD coverage
 * traded away the coverage the section already had — the
 * "repeated steering does not help, the addition keeps replacing"
 * report (2026-09-01 relook, docs/SPEC_GENERATION_RELOOK.md).
 */
import React, { useState } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';

export default function ClaimAnchorWarningBanner({
  sectionId,
}: {
  sectionId: string;
}): JSX.Element | null {
  const entry = useSpecStore((s) => s.anchorWarnings[sectionId]);
  const dismiss = useSpecStore((s) => s.dismissAnchorWarning);
  const section = useSpecStore(
    (s) => s.sections.find((sec) => sec.id === sectionId) || null,
  );
  // Feature mappings give us the human-readable feature_text for
  // each Fn tag. The store loads them via featureMappings —
  // gracefully degrade to bare tag when not yet in cache.
  const featureMappings = useSpecStore(
    (s) => s.featureMappings || [],
  );
  const generateSection = useSpecStore((s) => s.generateSection);
  // Same gate ActionBar puts on Generate. Before this wave the CTA only
  // pre-filled steering, so the resulting generate went through that
  // gate; routing straight to cover_missing went around it and could
  // race a section generate already in flight or the Generate-
  // Everything worker writing this same section (2026-09-02 review).
  const generating = useSpecStore((s) => s.generateStatus === 'generating');
  const geStatus = useSpecStore((s) => s.generateEverythingStatus?.status);
  const geRunning = geStatus === 'queued' || geStatus === 'running';
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!entry || entry.missing.length === 0) return null;
  if (section?.is_frozen) return null;
  // Persisted per-section coverage — what the additive repair acts on —
  // exists only for the detailed description (see
  // _ANCHOR_OWNER_SECTION_KINDS). Elsewhere the banner is advisory and
  // the repair route is Regenerate.
  const canAddCoverage = section?.section_kind === 'detailed_desc_embodiments';
  const blocked = adding || generating || geRunning;

  const missingTags = entry.missing;
  // Scope copy: detailed-description checks both independent +
  // dependent; summary mirrors only independent claims. The
  // backend tells us which scope ran via dependent_features_
  // checked; the FE banner picks the matching wording.
  const scopeCopy = entry.dependent
    ? 'Independent and dependent claim features may lack support.'
    : 'Independent claim features may lack support.';

  const labelFor = (tag: string): string => {
    const row = featureMappings.find((f) => f.feature_tag === tag);
    if (!row) return tag;
    const text = (row.feature_text || '').trim();
    if (!text) return tag;
    // Compact the claim text for inline display — first comma /
    // semicolon, capped at ~60 chars. Keeps the bullet readable
    // even when the source claim is verbose.
    const head = text.split(/[,;.]/, 1)[0]?.trim() || text;
    const trimmed = head.length > 60 ? `${head.slice(0, 57)}…` : head;
    return `${tag} — ${trimmed}`;
  };

  const onAddCoverage = async () => {
    if (blocked) return;
    setAdding(true);
    setError(null);
    try {
      // Straight to the additive path — no steering detour, no
      // overwrite (2026-09-01 relook P0.2/P0.3).
      //
      // This button used to pre-fill the steering textarea and set
      // pendingRegenerateWithSteering, so the drafter's next
      // Regenerate ran with overwrite=true: DELETE every unlocked
      // paragraph, INSERT a fresh draft under the same word cap.
      // Adding the missing families therefore COST the families
      // already written, which is exactly what "the addition keeps
      // replacing" described. mode:'cover_missing' drafts support
      // for these features only and appends it.
      const outcome = await generateSection({
        sectionId,
        mode: 'cover_missing',
      });
      if (outcome.kind === 'error') {
        setError(outcome.message || 'Could not add coverage.');
      }
    } finally {
      setAdding(false);
    }
  };

  // Section-name suffix for the banner header so the drafter
  // never has to guess which section the gap belongs to. The
  // backend only ever emits anchor checks for
  // detailed_desc_embodiments + summary
  // (spec.py:_CLAIM_COVERAGE_SECTIONS) so the name resolution
  // below will only ever produce those two strings — keeping
  // the lookup defensive against a future section_kind
  // addition without the banner needing a code change.
  const sectionName = (() => {
    if (!section) return '';
    const customOrTitle = (section.custom_title || section.title || '').trim();
    if (customOrTitle) return customOrTitle;
    return (section.section_kind || '').replace(/_/g, ' ');
  })();

  return (
    <div
      className="mt-3 rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-zinc-800"
      role="alert"
      data-testid={`spec-anchor-warning-${sectionId}`}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle
          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600"
          aria-hidden
        />
        <div className="flex-1 space-y-1">
          <div className="font-medium text-amber-900">
            Claim coverage gap detected
            {sectionName && (
              <span className="font-normal text-amber-800">
                {' '}in {sectionName}
              </span>
            )}
          </div>
          <div
            className="text-zinc-700"
            data-testid={`spec-anchor-warning-scope-${sectionId}`}
          >
            {scopeCopy}
          </div>
          <ul
            className="ml-2 list-disc pl-3 text-zinc-700"
            data-testid={`spec-anchor-warning-list-${sectionId}`}
          >
            {missingTags.map((tag) => (
              <li key={tag}>{labelFor(tag)}</li>
            ))}
          </ul>
          {canAddCoverage && (
            <button
              type="button"
              className="mt-1 inline-flex items-center gap-1 rounded-sm border border-amber-400 bg-white px-2 py-0.5 text-amber-900 hover:bg-amber-100 disabled:opacity-50"
              onClick={onAddCoverage}
              disabled={blocked}
              title={
                geRunning
                  ? 'Generate Everything is running — wait for it to finish.'
                  : generating
                    ? 'A generation is already in progress.'
                    : 'Writes new paragraphs for these features and adds '
                      + 'them to the end of the section. Nothing already '
                      + 'written is changed.'
              }
              data-testid={`spec-anchor-warning-add-${sectionId}`}
            >
              {adding && <Loader2 className="h-3 w-3 animate-spin" />}
              {adding
                ? 'Adding coverage…'
                : 'Add coverage for these features →'}
            </button>
          )}
          {error && (
            <div
              className="mt-1 text-red-700"
              data-testid={`spec-anchor-warning-error-${sectionId}`}
            >
              {error}
            </div>
          )}
        </div>
        <button
          type="button"
          className="rounded-sm p-1 text-zinc-400 hover:bg-amber-100 hover:text-zinc-700"
          onClick={() => dismiss(sectionId)}
          aria-label="Dismiss claim coverage warning"
          data-testid={`spec-anchor-warning-dismiss-${sectionId}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
